/**
 * SA-1387 (spec §5.4): one mailbox in the pane. The header (name, status,
 * provider, every badge the row used to carry, the account line), the
 * everyday actions beside it and Delete in the ⋯ menu, a banner only when
 * something is wrong, then the tabs. The active tab's body is `children`.
 */
import type { ReactNode } from 'react'
import { Check, Link2, MoreHorizontal, Pause, Play, RefreshCw, Trash2, UserCog } from 'lucide-react'
import {
  Alert, AlertDescription, Badge, Button,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  Tabs, TabsContent, TabsList, TabsTrigger, Tip,
} from '@/components/ui'
import { DETAIL_TITLE_CLASS } from '@/components/layout/PageHeader'
import { cn } from '@/utils'
import { tourAnchor } from '@/components/tour/tourAnchor'
import type { Mailbox, MailboxProvider } from '@/services/mailboxes'
import { DIFFERENT_ACCOUNT, accountLine, flagTip } from './connectedAccountText'
import { groupLine, isGroupSource } from './groupSourceText'
import { attentionLine, type MailboxTab } from './mailboxListModel'
import {
  BINDING_WARNING_FALLBACK, BINDING_WARNING_LABEL, CHANGE_ACCOUNT, CONFIRM_ACCOUNT, MORE_ACTIONS, POST_COMMIT_TIP,
  STATUS_BADGE, TAB_LABEL, attentionBanner, moreActionsLabel, parkedText, parkedTip, postCommitText, providerLabel,
} from './mailboxesText'

interface MailboxPaneProps {
  mailbox: Mailbox
  tab: MailboxTab
  providers: readonly MailboxProvider[]
  /** This mailbox's own action error (connect, poll, pause, resume, outbound, delete, cancel load). */
  actionError: string | null
  /** WAHA refused this mailbox's delete; Delete anyway is offered beside it. */
  deleteRefused: string | null
  onTab: (tab: MailboxTab) => void
  onConnect: () => void
  /** SA-1422: an email mailbox asks which account's inbox it reads. */
  onChangeAccount: () => void
  /** SA-1422: the person says the typed address's mail arrives in the connected account's inbox. */
  onConfirmAccount: () => void
  onLinkGroup: () => void
  onTogglePause: () => void
  onPollNow: () => void
  onDelete: () => void
  onDeleteAnyway: () => void
  children: ReactNode
}

export function MailboxPane({
  mailbox: mb, tab, providers, actionError, deleteRefused,
  onTab, onConnect, onChangeAccount, onConfirmAccount, onLinkGroup, onTogglePause, onPollNow, onDelete, onDeleteAnyway,
  children,
}: MailboxPaneProps) {
  const badge = STATUS_BADGE[mb.status]
  const line = accountLine(mb)
  // SA-947 (spec P10): a WhatsApp group source is pushed, never polled, and sends nothing.
  const group = isGroupSource(mb)
  const canConnect = mb.status === 'unconnected' || mb.status === 'reauth_required'
  const canPoll = !group && (mb.status === 'active' || mb.status === 'paused')
  // SA-1422 (spec §4.5): every connected email mailbox, never a group source (its account is the desk's WhatsApp).
  const canChangeAccount = !group && (mb.status === 'active' || mb.status === 'paused' || mb.status === 'reauth_required')
  const flagged = !group && line.mismatch
  // Spec §5.4: the banner holds only attentionLine's items 1 to 3, the ones in the destructive
  // tone; none of them reads the setup sentence, so none is passed.
  const attention = attentionLine(mb, new Date(), '')
  const tabs: MailboxTab[] = mb.sink_type === 'router' ? ['overview', 'routing', 'triage'] : ['overview', 'routing']

  return (
    <div>
      <div className="space-y-3 p-4" {...tourAnchor('mailboxes.pane.header')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn(DETAIL_TITLE_CLASS, '[overflow-wrap:anywhere]')}>{mb.display_name}</h2>
              {mb.status === 'reauth_required' && mb.last_error ? (
                <Tip text={mb.last_error}>
                  <Badge variant={badge.variant} tabIndex={0}>{badge.label}</Badge>
                </Tip>
              ) : (
                <Badge variant={badge.variant}>{badge.label}</Badge>
              )}
              <Badge variant="outline">{providerLabel(mb.provider, providers)}</Badge>
              {flagged && (
                <>
                  <Tip text={flagTip(mb)}>
                    <Badge variant="destructive" tabIndex={0}>{DIFFERENT_ACCOUNT}</Badge>
                  </Tip>
                  <Button variant="outline" size="sm" onClick={onConfirmAccount} leftIcon={<Check className="w-4 h-4" />}>
                    {CONFIRM_ACCOUNT}
                  </Button>
                </>
              )}
              {mb.send_reconsent_needed && (
                <Badge variant="warning">Re-consent needed for outbound</Badge>
              )}
              {mb.binding_warning && (
                <Badge variant="destructive">
                  {BINDING_WARNING_LABEL[mb.binding_warning] ?? BINDING_WARNING_FALLBACK}
                </Badge>
              )}
              {(mb.parked_count ?? 0) > 0 && (
                <Tip text={parkedTip(mb.last_parked_at)}>
                  <Badge variant="warning" tabIndex={0}>{parkedText(mb.parked_count as number)}</Badge>
                </Tip>
              )}
              {(mb.post_commit_failure_count ?? 0) > 0 && (
                <Tip text={POST_COMMIT_TIP}>
                  <Badge variant="warning" tabIndex={0}>{postCommitText(mb.post_commit_failure_count as number)}</Badge>
                </Tip>
              )}
            </div>
            <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {group ? groupLine(mb) : line.text}
              {mb.last_polled_at &&
                ` · last polled ${new Date(mb.last_polled_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canConnect && (
              <Button variant="outline" size="sm" onClick={onConnect} leftIcon={<Link2 className="w-4 h-4" />}>
                Connect
              </Button>
            )}
            {canChangeAccount && (
              <Button variant="ghost" size="sm" onClick={onChangeAccount} leftIcon={<UserCog className="w-4 h-4" />}>
                {CHANGE_ACCOUNT}
              </Button>
            )}
            {/* SA-947: a linked group row opens the dialog too, for Change group and
                for a status read that notices a lost session (final review). */}
            {group && (mb.status === 'connecting' || mb.status === 'active' || mb.status === 'paused') && (
              <Button variant="outline" size="sm" onClick={onLinkGroup} leftIcon={<Link2 className="w-4 h-4" />}>
                {mb.status === 'connecting' ? 'Continue linking' : 'Link and group'}
              </Button>
            )}
            {(mb.status === 'active' || mb.status === 'reauth_required') && (
              <Button variant="ghost" size="sm" onClick={onTogglePause} leftIcon={<Pause className="w-4 h-4" />}>
                Pause
              </Button>
            )}
            {mb.status === 'paused' && (
              <Button variant="ghost" size="sm" onClick={onTogglePause} leftIcon={<Play className="w-4 h-4" />}>
                Resume
              </Button>
            )}
            {canPoll && (
              <Button variant="ghost" size="sm" onClick={onPollNow} leftIcon={<RefreshCw className="w-4 h-4" />}>
                Poll now
              </Button>
            )}
            <DropdownMenu>
              <Tip text={MORE_ACTIONS}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label={moreActionsLabel(mb.display_name)}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
              </Tip>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {attention.tone === 'destructive' && (
          <Alert variant="destructive">
            <AlertDescription>{attentionBanner(attention.text, mb.status)}</AlertDescription>
          </Alert>
        )}
        {actionError && (
          <Alert variant="destructive">
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}
        {deleteRefused && (
          <Alert variant="destructive">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{deleteRefused} Nothing was deleted.</span>
              <Button variant="outline" size="sm" onClick={onDeleteAnyway}>Delete anyway</Button>
            </AlertDescription>
          </Alert>
        )}
      </div>
      {/* Manual activation: an arrow key only moves focus, so a tab is asked for once, by a click or Enter. */}
      <Tabs value={tab} onValueChange={(value) => onTab(value as MailboxTab)} activationMode="manual">
        <div className="px-4">
          <TabsList>
            {tabs.map((t) => <TabsTrigger key={t} value={t} {...(t === 'routing' ? tourAnchor('mailboxes.pane.routing') : {})}>{TAB_LABEL[t]}</TabsTrigger>)}
          </TabsList>
        </div>
        <TabsContent value={tab} className="p-4">
          {children}
        </TabsContent>
      </Tabs>
    </div>
  )
}
